// @ts-nocheck
/* eslint-disable react/prop-types */
/**
 * DiamondNode — Diamond / rhombus-shaped node for SolyticsFlow.
 *
 * Rotated square with horizontal text overlay.
 * Typically used for branching / decision points.
 *
 * Supports:
 *   data.label       — display text
 *   data.color       — fill/background override (theme tokens supported)
 *   data.borderColor — border override
 *   data.textColor   — label colour override
 *   data.colorScheme — convenience fallback preset (default: 'info')
 *   data.tooltip     — string | ReactNode
 *   data.menuItems   — SolyticsMenus item array
 */
import React, { memo, useMemo } from 'react';
import { Position } from 'reactflow';
import { useTheme } from '@mui/material/styles';
import SolyticsBox from '../../../SolyticsBox';
import NodeHandles from '../shared/NodeHandles';
import NodeTooltip from '../shared/NodeTooltip';
import NodeMenu from '../shared/NodeMenu';
import { NODE_COLOR_SCHEMES } from '../constants';
import { resolveNodeColors } from '../shared/themeColorUtils';
import SolyticsTypography from '../../../SolyticsTypography';

export const DiamondShape = memo(({ label, colors }) => {
  return (
    <SolyticsBox
      width={100}
      height={100}
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
    >
      <SolyticsBox
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 72,
          height: 72,
          transform: 'translate(-50%, -50%) rotate(45deg)',
          border: `1.5px solid ${colors.border}`,
          borderRadius: '6px',
          bgcolor: colors.bg,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <SolyticsBox position="relative" zIndex={2} maxWidth="60px">
        <SolyticsTypography
          variant="caption"
          color={colors.text}
          align="center"
          noWrap
        >
          {label}
        </SolyticsTypography>
      </SolyticsBox>
    </SolyticsBox>
  );
});
DiamondShape.displayName = 'DiamondShape';

const DiamondNode = memo(({ id, data, parentId }) => {
  const theme = useTheme();

  const colors = useMemo(() => {
    return resolveNodeColors(theme, data, NODE_COLOR_SCHEMES, 'info');
  }, [data, theme]);

  const defaultHandles = useMemo(
    () => [
      { type: 'target', position: Position.Top, id: 'target-top' },
      { type: 'source', position: Position.Bottom, id: 'source-bottom' },
      { type: 'target', position: Position.Left, id: 'target-left' },
      { type: 'source', position: Position.Right, id: 'source-right' },
    ],
    []
  );

  return (
    <NodeMenu menuItems={data?.menuItems} nodeId={id} parentId={parentId}>
      <NodeTooltip tooltip={data?.tooltip}>
        <SolyticsBox display="inline-flex">
          <DiamondShape label={data?.label} colors={colors} />
        </SolyticsBox>
      </NodeTooltip>

      <NodeHandles handles={data?.handles} fallbackHandles={defaultHandles} />
    </NodeMenu>
  );
});
DiamondNode.displayName = 'DiamondNode';

export default DiamondNode;
